import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type SubscriptionStatus = "pending" | "authorized" | "paused" | "cancelled";

export interface BudgetSubscription {
  id: string;
  budget_id: string;
  client_id: string | null;
  lead_id: string | null;
  amount: number;
  currency: string;
  frequency: number;
  frequency_type: string;
  status: SubscriptionStatus;
  mp_preapproval_id: string | null;
  init_point: string | null;
  payer_email: string | null;
  reason: string | null;
  next_payment_date: string | null;
  started_at: string | null;
  canceled_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Most recent subscription for a budget — a budget has at most one active (pending/authorized/paused) at a time. */
export function useBudgetSubscription(budgetId: string | null | undefined) {
  return useQuery({
    queryKey: ["budget-subscription", budgetId],
    queryFn: async () => {
      if (!budgetId) return null;
      const { data, error } = await supabase
        .from("budget_subscriptions" as never)
        .select("*")
        .eq("budget_id", budgetId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as BudgetSubscription | null;
    },
    enabled: !!budgetId,
  });
}

export function useCreateSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (budgetId: string) => {
      const { data, error } = await supabase.functions.invoke("mp-create-subscription", {
        body: { budget_id: budgetId },
      });
      if (error) {
        const body = await (error as { context?: Response }).context?.json?.().catch(() => null);
        if (body?.error === "recurring_in_barter")
          throw new Error(
            'a mensalidade desta proposta está incluída na permuta (ligue "Cobrar a mensalidade mesmo assim" na proposta)',
          );
        throw error;
      }
      return data as { id: string; init_point: string };
    },
    onSuccess: (_data, budgetId) => {
      qc.invalidateQueries({ queryKey: ["budget-subscription", budgetId] });
      toast.success("Cobrança mensal criada — envie o link para o cliente autorizar.");
    },
    onError: (e: Error) => toast.error("Erro ao criar cobrança mensal: " + e.message),
  });
}

export function useManageSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      subscription_id: string;
      budget_id: string;
      action: "pause" | "resume" | "cancel";
    }) => {
      const { error } = await supabase.functions.invoke("mp-manage-subscription", {
        body: { subscription_id: args.subscription_id, action: args.action },
      });
      if (error) throw error;
      return args;
    },
    onSuccess: (args) => {
      qc.invalidateQueries({ queryKey: ["budget-subscription", args.budget_id] });
      qc.invalidateQueries({ queryKey: ["finance-stats"] });
      const labels = {
        pause: "Cobrança pausada.",
        resume: "Cobrança retomada.",
        cancel: "Cobrança cancelada.",
      };
      toast.success(labels[args.action]);
    },
    onError: (e: Error) => toast.error("Erro ao atualizar cobrança mensal: " + e.message),
  });
}
